import { useEffect, useState } from "react";
import { useAuthContext } from "@acto/web-common/auth";
import { useLocale } from "@acto/web-common/locales";
import { useTranscribe } from "@acto/taskpane/core/features/transcription/useTranscribe";

export interface UseRecapTranscriptionResult {
  recording: boolean;
  start: () => void;
  stop: () => void;
  /** Transcript of the current/last recording session only; resets to "" on each start. */
  sessionText: string;
  /** Seconds elapsed in the current session; resets to 0 on each start. */
  elapsedSeconds: number;
}

/**
 * Recap voice transcription. Wraps the shared field-sales transcribe engine (WebSocket +
 * AudioWorklet streaming to the intelligence layer) and layers on a session-scoped elapsed
 * timer. Returns the live session text only — callers splice it onto their own content, so the
 * engine stays free of recap business logic. Tenant/user are read from the auth context.
 */
export function useRecapTranscription(): UseRecapTranscriptionResult {
  const { user } = useAuthContext();
  const { locale } = useLocale();
  const [sessionText, setSessionText] = useState("");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const { isRecording, startRecording, stopRecording } = useTranscribe({
    onTranscript: setSessionText,
    tenant: user?.tenantId,
    user: user?.email,
    language: locale,
  });

  useEffect(() => {
    if (!isRecording) return;
    const id = window.setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [isRecording]);

  const start = () => {
    setSessionText("");
    setElapsedSeconds(0);
    void startRecording();
  };

  const stop = () => {
    void stopRecording();
  };

  return { recording: isRecording, start, stop, sessionText, elapsedSeconds };
}
